import React from 'react'
import { Button, Checkbox, Form, Input, Select, Switch, message } from 'antd'
import {
  EDeliveryRestriction,
  ESubCategoryStatus,
  IFrontCategory,
  IFrontSubCategory,
  ISubCategory
} from 'models/product'
import { productApi } from 'api/product'
import { showServerError } from 'shared/utils/showServerError'
import { DELIVERY_RESTRICTION_OPTIONS } from 'shared/constants/deliveryRestrictions'
import { SeoFields } from 'components/ui'
import { ChangeDataWrapper } from '../ChangeDataWrapper/ChangeDataWrapper'

import styles from './ChangeSubCategory.module.scss'

interface IChangeSubCategoryProps {
  currentSubCategory: IFrontSubCategory
  categories: IFrontCategory[]
  onSuccess: (updatedSubCategory: ISubCategory) => void
  onDeleteSuccess: (subCategoryId: string) => void
}

interface IFormData {
  name: string
  categoryId: string
  is_not_returnable: boolean
  is_visible: boolean
  delivery_restriction?: EDeliveryRestriction
  seoInfo?: {
    title?: string
    desc?: string
    h1?: string
    seoText?: string
  }
}

export const ChangeSubCategory: React.FC<IChangeSubCategoryProps> = React.memo(
  ({ currentSubCategory, categories, onSuccess, onDeleteSuccess }) => {
    const [isLoading, setIsLoading] = React.useState(false)
    const [isDeleting, setIsDeleting] = React.useState(false)
    const [form] = Form.useForm()

    const isOnModeration =
      currentSubCategory.status === ESubCategoryStatus.MODERATION

    const normalizedCategories: IFrontCategory[] = React.useMemo(() => {
      return categories.filter((item) => !item.isNew)
    }, [categories])

    const getCurrentSubCategoryVisibility = React.useCallback(() => {
      return (
        (currentSubCategory as unknown as { is_visible?: boolean })
          ?.is_visible ?? true
      )
    }, [currentSubCategory])

    const onSubmit = async (data: IFormData) => {
      try {
        setIsLoading(true)
        const commonData = {
          name: data.name,
          category_id: data.categoryId,
          is_returnable: !data.is_not_returnable,
          delivery_restriction: data.delivery_restriction ?? null,
          title: data?.seoInfo?.title,
          desc: data?.seoInfo?.desc,
          h1: data?.seoInfo?.h1,
          seoText: data?.seoInfo?.seoText
        }
        if (currentSubCategory.isNew) {
          let createdSubCategory = (
            await productApi.createSubCategory(commonData)
          ).data

          if (!data.is_visible) {
            createdSubCategory = (
              await productApi.toggleSubCategoryVisibility(
                createdSubCategory._id,
                false
              )
            ).data
          }

          onSuccess(createdSubCategory)
          message.success('Подкатегория успешно добавлена')
        } else {
          let updatedSubCategory = (
            await productApi.updateSubCategory(
              currentSubCategory._id,
              commonData
            )
          ).data

          if (getCurrentSubCategoryVisibility() !== data.is_visible) {
            updatedSubCategory = (
              await productApi.toggleSubCategoryVisibility(
                currentSubCategory._id,
                data.is_visible
              )
            ).data
          }

          onSuccess(updatedSubCategory)

          message.success('Подкатегория успешно обновлена')
        }
      } catch (e) {
        showServerError(e)
      } finally {
        setIsLoading(false)
      }
    }

    const onDelete = async () => {
      try {
        setIsDeleting(true)
        if (!currentSubCategory.isNew) {
          await productApi.deleteSubCategory(currentSubCategory._id)
        }
        onDeleteSuccess(currentSubCategory._id)
      } catch (e) {
        showServerError(e)
      } finally {
        setIsDeleting(false)
      }
    }

    React.useEffect(() => {
      form.resetFields()
      form.setFieldsValue({
        name: currentSubCategory.name,
        categoryId: currentSubCategory?.category_id?._id,
        is_not_returnable: !currentSubCategory?.is_returnable,
        is_visible: getCurrentSubCategoryVisibility(),
        delivery_restriction:
          currentSubCategory?.delivery_restriction ?? undefined,
        seoInfo: {
          title: currentSubCategory?.seoInfo?.title,
          desc: currentSubCategory?.seoInfo?.desc,
          h1: currentSubCategory?.seoInfo?.h1,
          seoText: currentSubCategory?.seoInfo?.seoText
        }
      })
    }, [currentSubCategory, getCurrentSubCategoryVisibility])

    return (
      <ChangeDataWrapper
        title={isOnModeration ? 'Подкатегория (подтверждение)' : 'Подкатегория'}
        isOnModeration={isOnModeration}
        wrapperClassName={styles.wrapper}
      >
        <Form
          form={form}
          name='changeSubCategory'
          size='small'
          onFinish={onSubmit}
        >
          <Form.Item
            label='Название подкатегории'
            name='name'
            rules={[
              {
                required: true,
                message: 'Введите название'
              },
              {
                whitespace: true,
                message: 'Введите название'
              }
            ]}
            hasFeedback
          >
            <Input />
          </Form.Item>
          <Form.Item
            name='categoryId'
            label='Основная категория'
            rules={[
              {
                required: true,
                message: 'Выберите категорию'
              }
            ]}
            hasFeedback
          >
            <Select
              options={normalizedCategories.map((item) => ({
                value: item._id,
                label: item.name
              }))}
            />
          </Form.Item>
          <Form.Item name='is_not_returnable' valuePropName='checked'>
            <Checkbox>Невозвратный товар</Checkbox>
          </Form.Item>
          <Form.Item
            label='Не принимает Яндекс Доставка'
            name='delivery_restriction'
            tooltip='Яндекс Доставка не возит корма, ветпрепараты и БАД, ядовитые вещества. Для товаров подкатегории с причиной ПВЗ Яндекса и 5Post на сайте и в приложении будут недоступны, а в карточке товара продавец увидит предупреждение с цитатой из правил Яндекса'
          >
            <Select
              allowClear
              placeholder='Ограничений нет'
              options={DELIVERY_RESTRICTION_OPTIONS}
            />
          </Form.Item>
          <Form.Item
            label='Отображать в выдаче'
            name='is_visible'
            valuePropName='checked'
          >
            <Switch />
          </Form.Item>
          <SeoFields
            inputClassName={styles.formInput}
            textareaClassName={styles.formTextarea}
            panelHeaderClassName={styles.panelHeader}
          />
          <div className={styles.footer}>
            <Button
              htmlType='submit'
              type='primary'
              size='middle'
              loading={isLoading}
            >
              {isOnModeration
                ? 'Подтвердить и сохранить'
                : 'Сохранить изменения'}
            </Button>
            <Button
              danger
              htmlType='button'
              type='primary'
              size='middle'
              loading={isDeleting}
              onClick={onDelete}
            >
              Удалить
            </Button>
          </div>
        </Form>
      </ChangeDataWrapper>
    )
  }
)
